import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';

interface SelectionGatewayProps {
    onSelectSection: (section: 'agentic' | 'brand') => void;
    onGoHome: () => void;
    isVisible: boolean;
}

const SelectionGateway: React.FC<SelectionGatewayProps> = ({ onSelectSection, onGoHome, isVisible }) => {
    const [hoveredSection, setHoveredSection] = useState<'agentic' | 'brand' | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Hover preview animation
    useEffect(() => {
        if (!containerRef.current) return;

        const agenticSide = containerRef.current.querySelector('.agentic-side');
        const brandSide = containerRef.current.querySelector('.brand-side');

        const ANIMATION_CONFIG = { duration: 0.4, ease: 'power2.out' };

        if (hoveredSection === 'agentic') {
            gsap.to(agenticSide, { flex: 1.4, ...ANIMATION_CONFIG }); // Slightly more expansion
            gsap.to(brandSide, { flex: 0.6, ...ANIMATION_CONFIG });
        } else if (hoveredSection === 'brand') {
            gsap.to(agenticSide, { flex: 0.6, ...ANIMATION_CONFIG });
            gsap.to(brandSide, { flex: 1.4, ...ANIMATION_CONFIG });
        } else {
            gsap.to([agenticSide, brandSide], { flex: 1, ...ANIMATION_CONFIG });
        }
    }, [hoveredSection]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    ref={containerRef}
                    className="selection-gateway"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    onMouseLeave={() => setHoveredSection(null)}
                >
                    {/* Agentic Systems Side */}
                    <motion.div
                        className="agentic-side gateway-card"
                        onMouseEnter={() => setHoveredSection('agentic')}
                        onClick={() => onSelectSection('agentic')}
                        whileTap={{ scale: 0.98 }}
                    >
                        {/* Animated circuit background */}
                        <div className="gateway-bg agentic-bg">
                            <div className="circuit-lines" />
                            <div className="data-particles" />
                        </div>

                        <div className="gateway-content">
                            <motion.div
                                className="gateway-icon"
                                animate={hoveredSection === 'agentic' ? { rotate: 360 } : { rotate: 0 }}
                                transition={{ duration: 2, repeat: hoveredSection === 'agentic' ? Infinity : 0, ease: 'linear' }}
                            >
                                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="32" cy="32" r="28" stroke="#a3ff00" strokeWidth="2" strokeDasharray="4 4" />
                                    <circle cx="32" cy="32" r="20" stroke="#a3ff00" strokeWidth="2" />
                                    <circle cx="32" cy="32" r="8" fill="#a3ff00" />
                                    <line x1="32" y1="4" x2="32" y2="12" stroke="#a3ff00" strokeWidth="2" />
                                    <line x1="32" y1="52" x2="32" y2="60" stroke="#a3ff00" strokeWidth="2" />
                                    <line x1="4" y1="32" x2="12" y2="32" stroke="#a3ff00" strokeWidth="2" />
                                    <line x1="52" y1="32" x2="60" y2="32" stroke="#a3ff00" strokeWidth="2" />
                                </svg>
                            </motion.div>

                            <h2 className="gateway-title">FACTORY CONTROL SYSTEMS</h2>
                            <p className="gateway-tagline">HMI/SCADA dashboards, AI automation & real-time monitoring</p>

                            <div className="gateway-preview">
                                <span className="preview-tag">13+ DEPLOYED | AI-POWERED | ENTERPRISE</span>
                            </div>

                            <motion.button
                                className="gateway-cta agentic-cta"
                                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(163, 255, 0, 0.5)' }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span>VIEW SYSTEMS →</span>
                            </motion.button>

                            {/* Terminal preview on hover - MOVED INSIDE CONTENT */}
                            <AnimatePresence>
                                {hoveredSection === 'agentic' && (
                                    <motion.div
                                        className="hover-preview terminal-preview"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{
                                            opacity: 1,
                                            height: 'auto',
                                            transition: {
                                                height: { duration: 0.3, ease: "easeOut" },
                                                opacity: { duration: 0.2, delay: 0.1 }
                                            }
                                        }}
                                        exit={{
                                            opacity: 0,
                                            height: 0,
                                            transition: {
                                                height: { duration: 0.2, ease: "easeIn" },
                                                opacity: { duration: 0.1 }
                                            }
                                        }}
                                    >
                                        <div className="terminal-header">
                                            <span className="terminal-dot red" />
                                            <span className="terminal-dot yellow" />
                                            <span className="terminal-dot green" />
                                            <span className="terminal-title">system_preview.sh</span>
                                        </div>
                                        <div className="terminal-body">
                                            <p><span className="prompt">$</span> initializing_factory_controls...</p>
                                            <p><span className="success">✓</span> scada_link: CONNECTED</p>
                                            <p><span className="success">✓</span> plc_sync: ACTIVE</p>
                                            <p className="blink">_ awaiting_operator</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>

                    {/* Divider */}
                    <div className="gateway-divider">
                        <div className="divider-line top" />

                        <motion.button
                            className="home-pill"
                            onClick={(e) => { e.stopPropagation(); onGoHome(); }}
                            animate={{
                                x: hoveredSection === 'agentic' ? -90 : hoveredSection === 'brand' ? 90 : 0,
                                scale: hoveredSection ? 1.05 : 1
                            }}
                            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
                                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.5" />
                            </svg>
                        </motion.button>

                        <div className="divider-line bottom" />
                    </div>

                    {/* Brand Websites Side */}
                    <motion.div
                        className="brand-side gateway-card"
                        onMouseEnter={() => setHoveredSection('brand')}
                        onClick={() => onSelectSection('brand')}
                        whileTap={{ scale: 0.98 }}
                    >
                        {/* Elegant gradient background */}
                        <div className="gateway-bg brand-bg">
                            <div className="gradient-orb orb-1" />
                            <div className="gradient-orb orb-2" />
                            <div className="shimmer-overlay" />
                        </div>

                        <div className="gateway-content">
                            <motion.div
                                className="gateway-icon brand-icon"
                                animate={hoveredSection === 'brand' ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                                transition={{ duration: 1.5, repeat: hoveredSection === 'brand' ? Infinity : 0 }}
                            >
                                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="8" y="12" width="48" height="36" rx="4" stroke="#D4AF37" strokeWidth="2" />
                                    <rect x="12" y="16" width="40" height="24" fill="#D4AF37" fillOpacity="0.2" rx="2" />
                                    <circle cx="32" cy="52" r="3" fill="#D4AF37" />
                                    <path d="M20 28h24M20 32h16" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </motion.div>

                            <h2 className="gateway-title brand-title">PREMIUM WEB EXPERIENCES</h2>
                            <p className="gateway-tagline brand-tagline">Custom websites, e-commerce platforms & ordering systems (OFOS, RMS, POS)</p>

                            <div className="gateway-preview brand-preview">
                                <span className="preview-tag brand-tag">REAL-TIME | INTERACTIVE | MOBILE-OPTIMIZED</span>
                            </div>

                            <motion.button
                                className="gateway-cta brand-cta"
                                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(212, 175, 55, 0.5)' }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span>VIEW PORTFOLIO →</span>
                            </motion.button>

                            {/* Elegant preview on hover - MOVED INSIDE CONTENT */}
                            <AnimatePresence>
                                {hoveredSection === 'brand' && (
                                    <motion.div
                                        className="hover-preview elegant-preview"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{
                                            opacity: 1,
                                            height: 'auto',
                                            transition: {
                                                height: { duration: 0.3, ease: "easeOut" },
                                                opacity: { duration: 0.2, delay: 0.1 }
                                            }
                                        }}
                                        exit={{
                                            opacity: 0,
                                            height: 0,
                                            transition: {
                                                height: { duration: 0.2, ease: "easeIn" },
                                                opacity: { duration: 0.1 }
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'rgba(10, 10, 10, 0.6)', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.15)', fontFamily: 'monospace', fontSize: '0.75rem', color: '#e2e8f0', textAlign: 'left', width: '100%', marginTop: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255, 95, 86, 0.8)' }} />
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255, 189, 46, 0.8)' }} />
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(39, 201, 63, 0.8)' }} />
                                                </div>
                                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>Engine.tsx</span>
                                            </div>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                                                <span style={{ color: '#c678dd' }}>import</span> { '{' } <span style={{ color: '#e5c07b' }}>WebGL</span>, <span style={{ color: '#e5c07b' }}>GSAP</span> { '}' } <span style={{ color: '#c678dd' }}>from</span> <span style={{ color: '#98c379' }}>'@premium/core'</span>;
                                            </motion.div>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                                                <span style={{ color: '#c678dd' }}>const</span> <span style={{ color: '#e06c75' }}>projects</span> = <span style={{ color: '#d19a66' }}>9</span>; <span style={{ color: '#5c6370', fontStyle: 'italic' }}>// High-end</span>
                                            </motion.div>
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                                                <span style={{ color: '#61afef' }}>renderExperience</span>({ '{' } <span style={{ color: '#d19a66' }}>quality</span>: <span style={{ color: '#98c379' }}>'ultra'</span> { '}' });
                                            </motion.div>
                                            <motion.div style={{ height: '3px', background: 'rgba(212, 175, 55, 0.1)', marginTop: '6px', borderRadius: '2px', overflow: 'hidden' }}>
                                                <motion.div 
                                                    style={{ height: '100%', background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.8), transparent)', width: '200%' }}
                                                    initial={{ x: '-100%' }}
                                                    animate={{ x: '50%' }}
                                                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                                                />
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SelectionGateway;
